import React, { useState, useEffect, useCallback } from 'react';
import { crmJobsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Clock, DollarSign, TrendingUp, Play, Square, Trash2, Loader2, AlertTriangle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';

const fmt$ = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtHrs = (mins) => {
  if (!mins) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function JobProfitabilityCard({ jobId }) {
  const { isOwner, isOffice } = useAuth();
  const canManage = isOwner || isOffice;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOut, setClockingOut] = useState(false);
  const [openLogId, setOpenLogId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await crmJobsAPI.laborSummary(jobId);
      setData(res.data);
      // Check if current user has an open clock-in
      const logs = await crmJobsAPI.timeLogs(jobId);
      const open = (logs.data || []).find(l => !l.clock_out);
      setOpenLogId(open?.id || null);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleClockIn = async () => {
    setClockingIn(true);
    try {
      await crmJobsAPI.clockIn(jobId);
      toast.success('Clocked in');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock in');
    } finally {
      setClockingIn(false);
    }
  };

  const handleClockOut = async () => {
    setClockingOut(true);
    try {
      await crmJobsAPI.clockOut(jobId);
      toast.success('Clocked out');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clock out');
    } finally {
      setClockingOut(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await crmJobsAPI.deleteTimeLog(jobId, logId);
      toast.success('Time log deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <Card data-testid="job-profitability-card">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const hasProfit = data.revenue_cents > 0;
  const marginColor = data.gross_margin_pct >= 50 ? 'text-green-600' : data.gross_margin_pct >= 25 ? 'text-amber-600' : 'text-red-600';

  return (
    <Card data-testid="job-profitability-card">
      <CardHeader className="pb-1 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Job Profitability
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 px-4 pb-4 pt-1">
        {/* Cost Breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cost-breakdown">
          <div className="rounded-lg border p-2.5">
            <div className="text-[10px] text-muted-foreground mb-0.5">Revenue</div>
            <div className="text-base font-semibold tabular-nums" data-testid="profitability-revenue">{fmt$(data.revenue_cents)}</div>
          </div>
          <div className="rounded-lg border p-2.5">
            <div className="text-[10px] text-muted-foreground mb-0.5 flex items-center gap-1">
              Labor Cost
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger><Info className="w-3 h-3" /></TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-[200px]">
                      {data.time_source === 'actual' ? 'Based on Actual Labor Time (clocked hours)' :
                       data.time_source === 'scheduled' ? 'Based on Scheduled Duration (estimated — no clock data yet)' :
                       'No labor time recorded'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-base font-semibold tabular-nums" data-testid="profitability-labor-cost">{fmt$(data.labor_cost_cents)}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="profitability-labor-source">
              {data.time_source === 'actual' ? 'Actual Labor Time' : data.time_source === 'scheduled' ? 'Scheduled Duration Used' : 'No time data'}
            </div>
          </div>
          <div className="rounded-lg border p-2.5">
            <div className="text-[10px] text-muted-foreground mb-0.5">Material Cost</div>
            <div className="text-base font-semibold tabular-nums" data-testid="profitability-material-cost">{fmt$(data.material_cost_cents)}</div>
          </div>
          <div className="rounded-lg border p-2.5">
            <div className="text-[10px] text-muted-foreground mb-0.5">Total Direct Cost</div>
            <div className="text-base font-semibold tabular-nums" data-testid="profitability-direct-cost">{fmt$(data.total_direct_cost_cents)}</div>
          </div>
        </div>

        {/* Profit Bar */}
        {hasProfit && (
          <div className="flex items-center gap-4 p-3 rounded-lg bg-muted/40" data-testid="profit-summary">
            <div>
              <div className="text-xs text-muted-foreground">Gross Profit</div>
              <div className={`text-xl font-bold tabular-nums ${marginColor}`} data-testid="profitability-gross-profit">{fmt$(data.gross_profit_cents)}</div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <div className="text-xs text-muted-foreground">Gross Margin</div>
              <div className={`text-xl font-bold tabular-nums ${marginColor}`} data-testid="profitability-gross-margin">{data.gross_margin_pct}%</div>
            </div>
            <div className="h-8 w-px bg-border" />
            <div>
              <div className="text-xs text-muted-foreground">Labor Hours</div>
              <div className="text-lg font-semibold tabular-nums" data-testid="profitability-labor-hours">{fmtHrs(data.effective_minutes)}</div>
            </div>
          </div>
        )}

        {/* Technician Breakdown */}
        {data.technicians.length > 0 && (
          <div data-testid="tech-breakdown">
            <div className="text-xs font-medium text-muted-foreground mb-2">Labor by Technician</div>
            <div className="space-y-1.5">
              {data.technicians.map((t) => (
                <div key={t.user_id} className="flex items-center justify-between text-sm border rounded px-3 py-1.5" data-testid={`tech-labor-${t.user_id}`}>
                  <span className="font-medium">{t.name}</span>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <span className="tabular-nums">{fmtHrs(t.minutes)}</span>
                    {canManage && t.rate_cents > 0 && <span className="tabular-nums">${(t.rate_cents / 100).toFixed(0)}/hr</span>}
                    <span className="font-medium text-foreground tabular-nums">{fmt$(t.cost_cents)}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.source === 'actual' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {t.source === 'actual' ? 'Actual' : 'Scheduled'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Logs */}
        {data.time_logs.length > 0 && (
          <div data-testid="time-logs-section">
            <div className="text-xs font-medium text-muted-foreground mb-2">Time Logs</div>
            <div className="space-y-1">
              {data.time_logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between text-xs border rounded px-3 py-2" data-testid={`time-log-${log.id}`}>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="font-medium">{log.user_name}</span>
                    <span className="text-muted-foreground">
                      {new Date(log.clock_in).toLocaleDateString()} {new Date(log.clock_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      {log.clock_out && ` — ${new Date(log.clock_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="tabular-nums font-medium">{log.duration_minutes ? fmtHrs(log.duration_minutes) : 'In progress...'}</span>
                    {canManage && log.clock_out && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteLog(log.id)} data-testid={`delete-log-${log.id}`}>
                        <Trash2 className="w-3 h-3 text-muted-foreground hover:text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warning if no hourly rate set */}
        {data.technicians.some(t => t.rate_cents === 0) && (
          <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded px-3 py-2" data-testid="no-rate-warning">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Some technicians don't have hourly rates set. Set rates in Team Management for accurate labor costing.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
